import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { POST_TYPE_DEFINITIONS, type TPostType } from '@onesource/schemas';
import { useGetSpacePostTypes } from '@onesource/services';
import { useMemo, useState, type FC, type MouseEvent } from 'react';
import { MdOutlineGridView } from 'react-icons/md';

import { POST_TYPE_MENU_PAPER_SX, renderPostTypeMenuItem, sortPostTypeOptions } from '../post-type-select';
import { FilterTriggerButton } from './filter-trigger-button';

type Props = {
  /** Space whose posts' used types populate the options */
  spaceId: string;
  value?: TPostType[];
  onChange: (value: TPostType[] | undefined) => void;
};

/**
 * Multi-select post type filter — same styled menu as the composer's post
 * type select (colored icon tile, label + description, check when selected).
 * Toggling keeps the menu open; the trigger shows the selection inline (or a
 * count when multiple).
 *
 * Options are sourced from the types real posts in the space actually use (like
 * the "Posted by" filter) — fetched eagerly (cached 60s) so the options are
 * ready the instant the menu opens, and it never offers a type no post carries.
 */
export const PostTypeFilter: FC<Props> = ({ spaceId, value, onChange }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const selected = value ?? [];

  const { data: usedTypes } = useGetSpacePostTypes(spaceId);

  const sortedOptions = useMemo(
    () => sortPostTypeOptions(POST_TYPE_DEFINITIONS.filter(d => (usedTypes ?? []).includes(d.value))),
    [usedTypes],
  );

  const handleToggle = (type: TPostType) => {
    const next = selected.includes(type) ? selected.filter(t => t !== type) : [...selected, type];
    onChange(next.length > 0 ? next : undefined);
  };

  const selectedLabel =
    selected.length === 1
      ? POST_TYPE_DEFINITIONS.find(d => d.value === selected[0])?.label
      : selected.length > 1
        ? `${selected.length} selected`
        : undefined;

  return (
    <>
      <FilterTriggerButton
        label="Post type"
        icon={<MdOutlineGridView size={15} />}
        value={selectedLabel}
        onClick={(e: MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget)}
      />
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: POST_TYPE_MENU_PAPER_SX } }}
      >
        {sortedOptions.length > 0 ? (
          sortedOptions.map(def =>
            renderPostTypeMenuItem(def, selected.includes(def.value), () => handleToggle(def.value)),
          )
        ) : (
          <MenuItem disabled>No post types yet</MenuItem>
        )}
      </Menu>
    </>
  );
};
